import { ActionKeys } from '~/actions/constants';
import { ActionTypes } from '~/actions/index';
import CarrierAccount from '~/types/CarrierAccount';
import { RegStatus } from '~/types/CarrierAccountRegStatus';
import {
  extractRefreshableLastValue,
  getNotRequested,
  getRefreshFailed,
  getRefreshing,
  getRefreshSuccess,
} from '~/utils/fetchable';

export type CarrierAccountRegStatusMap = { [carrierAccountObjectId: string]: RegStatus };
export type CarrierModalStatus =
  | 'closed'
  | 'standalone'
  | 'slide'
  | 'ups-registration'
  | 'canada_post-registration'
  | 'dhl_express-registration';
export type CarrierAccountsState = {
  readonly carrierAccounts: readonly Readonly<CarrierAccount>[];
  readonly isFetchingCarrierAccounts: boolean;
  readonly isSavingCarrierAccount: boolean;
  readonly successfulSubmit: Maybe<CarrierAccount>;
  readonly successfulFetch: boolean;
  readonly errorMessage: string;
  readonly editingCarrierAccountId: Maybe<string>;
  readonly carrierAccountRegStatusMap: Refreshable<CarrierAccountRegStatusMap, string>;
  readonly modalStatus: CarrierModalStatus;
  readonly editingCarrierServiceLevelsError: string[];
  readonly totalMasterCarrierAccounts: number;
  readonly totalUserCarrierAccounts: number;
};

const initialState: CarrierAccountsState = {
  carrierAccounts: [],
  isFetchingCarrierAccounts: false,
  isSavingCarrierAccount: false,
  successfulSubmit: null,
  successfulFetch: false,
  errorMessage: '',
  editingCarrierAccountId: null,
  carrierAccountRegStatusMap: getNotRequested(),
  modalStatus: 'closed',
  editingCarrierServiceLevelsError: [],
  totalMasterCarrierAccounts: 0,
  totalUserCarrierAccounts: 0,
};

function carrierAccountsReducer(
  state: CarrierAccountsState = initialState,
  action: ActionTypes,
): CarrierAccountsState {
  switch (action.type) {
    case ActionKeys.CARRIER_ACCOUNTS_CLEAR_STATE:
      return {
        ...state,
        errorMessage: '',
      };
    case ActionKeys.CARRIER_ACCOUNTS_CLEAR_SUBMIT:
      return {
        ...state,
        successfulSubmit: null,
      };
    case ActionKeys.FETCH_CARRIER_ACCOUNTS:
      return {
        ...state,
        isFetchingCarrierAccounts: true,
      };
    case ActionKeys.FETCH_CARRIER_ACCOUNTS_SUCCESS:
      return {
        ...state,
        isFetchingCarrierAccounts: false,
        carrierAccounts: action.payload.carrierAccounts,
        successfulFetch: true,
      };
    case ActionKeys.FETCH_CARRIER_ACCOUNTS_ERROR:
      return {
        ...state,
        isFetchingCarrierAccounts: false,
        errorMessage: action.payload.error,
      };
    case ActionKeys.FETCH_CARRIER_ACCOUNT_REG_STATUSES:
      return {
        ...state,
        carrierAccountRegStatusMap: getRefreshing(state.carrierAccountRegStatusMap),
      };
    case ActionKeys.FETCH_CARRIER_ACCOUNT_REG_STATUSES_SUCCESS:
      const fetchedStatusMap: CarrierAccountRegStatusMap =
        action.payload.carrierAccountRegStatuses.reduce((mapResult, regStatus) => {
          mapResult[regStatus.carrierAccount] = regStatus.status;
          return mapResult;
        }, {});
      const previouslyFetchedStatuses = extractRefreshableLastValue(
        state.carrierAccountRegStatusMap,
      );
      return {
        ...state,
        carrierAccountRegStatusMap: getRefreshSuccess(state.carrierAccountRegStatusMap, {
          ...previouslyFetchedStatuses,
          ...fetchedStatusMap,
        }),
      };
    case ActionKeys.FETCH_CARRIER_ACCOUNT_REG_STATUSES_ERROR:
      return {
        ...state,
        carrierAccountRegStatusMap: getRefreshFailed(
          state.carrierAccountRegStatusMap,
          action.payload.error,
        ),
      };
    case ActionKeys.CONNECT_CARRIER_ACCOUNT:
      return {
        ...state,
        isSavingCarrierAccount: true,
        editingCarrierAccountId: null,
      };
    case ActionKeys.CONNECT_CARRIER_ACCOUNT_SUCCESS:
      const newCarrierAccount = action.payload.carrierAccount;
      return {
        ...state,
        carrierAccounts: [newCarrierAccount, ...state.carrierAccounts],
        isSavingCarrierAccount: false,
        successfulSubmit: newCarrierAccount,
        modalStatus: 'closed',
      };
    case ActionKeys.CONNECT_CARRIER_ACCOUNT_ERROR:
      return {
        ...state,
        isSavingCarrierAccount: false,
        successfulSubmit: null,
        errorMessage: action.payload.error,
      };
    case ActionKeys.EDIT_CARRIER_ACCOUNT:
      return {
        ...state,
        isSavingCarrierAccount: true,
        editingCarrierAccountId: action.payload.id,
      };
    case ActionKeys.EDIT_CARRIER_ACCOUNT_SUCCESS:
      const updatedCarrierAccount = action.payload.carrierAccount;

      // Update state's carrier accounts array with the updated carrier account
      const carrierAccounts = state.carrierAccounts.map((account: CarrierAccount) => {
        if (account.objectId === updatedCarrierAccount.objectId) {
          return updatedCarrierAccount;
        }
        return account;
      });

      return {
        ...state,
        carrierAccounts,
        isSavingCarrierAccount: false,
        successfulSubmit: updatedCarrierAccount,
        modalStatus: 'closed',
      };
    case ActionKeys.EDIT_CARRIER_ACCOUNT_ERROR:
      return {
        ...state,
        isSavingCarrierAccount: false,
        successfulSubmit: null,
        errorMessage: action.payload.error,
      };
    case ActionKeys.EDIT_CARRIER_ACCOUNT_SERVICE_LEVELS_ERROR:
      return {
        ...state,
        editingCarrierServiceLevelsError: action.payload,
      };
    case ActionKeys.CREATE_CARRIER_ACCOUNT:
      return {
        ...state,
        isSavingCarrierAccount: true,
        editingCarrierAccountId: null,
      };
    case ActionKeys.CREATE_CARRIER_ACCOUNT_SUCCESS:
      const createdCarrierAccount = action.payload.carrierAccount;

      // If an existing carrier was activated, treat this as an "edit" and update the existing state's carrier accounts
      let updatedAccount = false;

      const totalCarrierAccounts = state.carrierAccounts.map((account: CarrierAccount) => {
        if (account.objectId === createdCarrierAccount.objectId) {
          updatedAccount = true;
          return createdCarrierAccount;
        }
        return account;
      });

      return {
        ...state,
        carrierAccounts: updatedAccount
          ? totalCarrierAccounts
          : [createdCarrierAccount, ...state.carrierAccounts],
        isSavingCarrierAccount: false,
        successfulSubmit: action.payload.carrierAccount,
        modalStatus: 'closed',
      };
    case ActionKeys.CREATE_CARRIER_ACCOUNT_ERROR:
      return {
        ...state,
        isSavingCarrierAccount: false,
        successfulSubmit: null,
        errorMessage: action.payload.error,
      };
    case ActionKeys.TOGGLE_CARRIER_MODAL:
      return {
        ...state,
        modalStatus: action.payload.modalStatus,
        // Clear the following if we are opening modal
        errorMessage: action.payload.modalStatus !== 'closed' ? '' : state.errorMessage,
        successfulSubmit: action.payload.modalStatus !== 'closed' ? null : state.successfulSubmit,
        editingCarrierAccountId:
          action.payload.modalStatus !== 'closed' ? null : state.editingCarrierAccountId,
        editingCarrierServiceLevelsError:
          action.payload.modalStatus !== 'closed' ? [] : state.editingCarrierServiceLevelsError,
      };
    default:
      return state;
  }
}

export default carrierAccountsReducer;
